// =============================================
// Boundary Enforcer Service
// v3.0: Auto-SBD from scope_in/out, violation detection
// =============================================

import { supabase } from "@/integrations/supabase/client";
import type { SemanticBoundary, ThematicNode } from "@/types/thematicMaps";

// =============================================
// Fuzzy matching (Jaccard similarity)
// =============================================

function tokenize(text: string): Set<string> {
  return new Set(
    text
      .toLowerCase()
      .replace(/[^\w\sąćęłńóśźż]/g, "")
      .split(/\s+/)
      .filter((w) => w.length > 2),
  );
}

function jaccardSimilarity(a: Set<string>, b: Set<string>): number {
  if (a.size === 0 && b.size === 0) return 0;
  const intersection = new Set([...a].filter((x) => b.has(x)));
  const union = new Set([...a, ...b]);
  return intersection.size / union.size;
}

// =============================================
// Boundary operations
// =============================================

export interface BoundaryViolation {
  nodeId: string;
  nodeTitle: string;
  violationType: "not_owned" | "adjacent_creep";
  matchedBoundary: string;
  similarity: number;
  reason: string;
}

/**
 * Build SBD boundaries from existing scope_in/scope_out.
 * scope_in -> owned, scope_out -> not_owned, entities overlap -> adjacent.
 */
export async function buildBoundaries(mapId: string): Promise<SemanticBoundary> {
  const { data: map } = await (supabase as any)
    .from("thematic_maps")
    .select("scope_in, scope_out")
    .eq("id", mapId)
    .single();

  if (!map) throw new Error("Mapa nie znaleziona");

  const scopeIn: string[] = Array.isArray(map.scope_in) ? map.scope_in : [];
  const scopeOut: string[] = Array.isArray(map.scope_out) ? map.scope_out : [];

  // Fetch entities to derive adjacent topics
  const { data: entities } = await (supabase as any)
    .from("thematic_entities")
    .select("canonical_name, entity_type")
    .eq("map_id", mapId);

  // Adjacent: entities that are tools/methods but not directly in scope_in
  const adjacent: string[] = [];
  if (entities) {
    for (const entity of entities) {
      if (entity.entity_type === "tool" || entity.entity_type === "method") {
        const isInScope = scopeIn.some(
          (s: string) =>
            s.toLowerCase().includes(entity.canonical_name.toLowerCase()) ||
            entity.canonical_name.toLowerCase().includes(s.toLowerCase()),
        );
        if (!isInScope) {
          adjacent.push(entity.canonical_name);
        }
      }
    }
  }

  const boundaries: SemanticBoundary = {
    owned: scopeIn,
    not_owned: scopeOut,
    adjacent: [...new Set(adjacent)].slice(0, 10),
  };

  // Save to DB
  await (supabase as any)
    .from("thematic_maps")
    .update({
      boundaries_owned: boundaries.owned,
      boundaries_not_owned: boundaries.not_owned,
      boundaries_adjacent: boundaries.adjacent,
      boundaries_updated_at: new Date().toISOString(),
    })
    .eq("id", mapId);

  return boundaries;
}

/**
 * Check nodes against boundaries for violations.
 * Returns violations (non-mutating).
 */
export function checkBoundaryViolations(
  nodes: ThematicNode[],
  boundaries: SemanticBoundary,
): BoundaryViolation[] {
  const violations: BoundaryViolation[] = [];
  const VIOLATION_THRESHOLD = 0.4;
  const ADJACENT_THRESHOLD = 0.3;

  for (const node of nodes) {
    const nodeTokens = tokenize(node.title + " " + node.canonical_question);

    // Check against not_owned
    for (const notOwned of boundaries.not_owned) {
      const boundaryTokens = tokenize(notOwned);
      const sim = jaccardSimilarity(nodeTokens, boundaryTokens);
      if (sim >= VIOLATION_THRESHOLD) {
        violations.push({
          nodeId: node.id,
          nodeTitle: node.title,
          violationType: "not_owned",
          matchedBoundary: notOwned,
          similarity: sim,
          reason: `Node "${node.title}" narusza granicę "${notOwned}" (podobieństwo: ${(sim * 100).toFixed(0)}%)`,
        });
      }
    }

    // Check for adjacent creep
    for (const adj of boundaries.adjacent) {
      const adjTokens = tokenize(adj);
      const sim = jaccardSimilarity(nodeTokens, adjTokens);
      if (sim >= ADJACENT_THRESHOLD) {
        violations.push({
          nodeId: node.id,
          nodeTitle: node.title,
          violationType: "adjacent_creep",
          matchedBoundary: adj,
          similarity: sim,
          reason: `Node "${node.title}" zbliża się do adjacent "${adj}" (podobieństwo: ${(sim * 100).toFixed(0)}%)`,
        });
      }
    }
  }

  return violations;
}

/**
 * Get current boundaries for a map.
 */
export async function getBoundaries(mapId: string): Promise<SemanticBoundary | null> {
  const { data } = await (supabase as any)
    .from("thematic_maps")
    .select("boundaries_owned, boundaries_not_owned, boundaries_adjacent")
    .eq("id", mapId)
    .single();

  if (!data) return null;

  return {
    owned: data.boundaries_owned || [],
    not_owned: data.boundaries_not_owned || [],
    adjacent: data.boundaries_adjacent || [],
  };
}
